import { Component, OnInit, OnDestroy, HostBinding, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
	FormBuilder,
	FormGroup,
	FormControl,
	Validators
} from '@angular/forms';
import {
	AuthenticationService,
	LocalDataService,
	AlertService,
	UtilsService,
	PaymentsService,
	CustomModalService
} from '../_services/index';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { GooglePlaceDirective } from 'ngx-google-places-autocomplete';
import { fadeInAnimation } from '../_animations/index';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
// Stripe
import { StripeService, Elements, Element as StripeElement, ElementsOptions } from 'ngx-stripe';
import { AngularFireFunctions } from 'angularfire2/functions';

const cardStyle = {
	base: {
		fontFamily: "Lato, 'Helvetica Neue', Arial, Helvetica, sans-serif",
	  	fontSmoothing: 'antialiased',
	  	fontWeight: '400',
		fontSize: '13px',
	  	'::placeholder': {
        	color: '#c6c6c6'
      	}
	}
};

@Component({
	selector: 'app-account',
	templateUrl: './account.component.html',
	styleUrls: ['./account.component.scss', '../login/login.component.scss'],
	animations: [fadeInAnimation]
})
export class AccountComponent implements OnInit, OnDestroy {
	// @HostBinding('@slideInOutAnimation') slideInOutAnimation = true;
	@HostBinding('@fadeInAnimation') fadeInAnimation = true;
	@HostBinding('style.display') display = 'block';

	@ViewChild('placesRef') placesRef: GooglePlaceDirective;
	@ViewChild('cardName') cardNameRef: ElementRef;

	// Stripe 
	elements: Elements;
	card: StripeElement; 
	cardError = false;
	cardPopulated = false;
	updatingCreditCard = false;
	allValid = false;
	cardValid = false;
	models: any = {
		optOutCheck: false,
		ageCheck: false
	};
	cardErrorMessage: string;
	elementsOptions: ElementsOptions = {
		locale: 'en'
	};

	activeField: string;
	defaultPaymentMethod: any = {};
	creditCardPlaceholder = { cardName: null, expDate: null, icon: null }; // While updates are loading in FB/Stripe
	fbUserName: any = {};
	userData: any = {};
	addressInfo: any = {};
	accountForm: any = {};
	changePasswordForm: any = {};
	addressForm: any = {};
	billingForm: any = {};
	defaultPaymentNameSnapshot: string;
	states: Array<{ abbr: string; name: string }>;
	cardTypes: Array<{ name: string; iconClass: string }>;
	phoneTypes: Array<any>;
	months: Array<{ val: number; name: string }>;
	inputMasks: any = {};
	placesAutoCompleteOptions: any = {};
	newUser: boolean;

	lockRoute = false; // If registration completion is required
	primaryPhoneError = false;
	formsInitialized = false;
	showPasswordField = false;
	showCurrentPassword = false;
	showNewPassword = false;
	showNewConfirmPassword = false;
	passwordUpdateButtonClicked = false;
	registerSubmitClicked = false;
	loadingCreditCard = false; // This will be true when Stripe API request is occuring, before the observable updates

	// Subscriptions
	userSubscription: Subscription;
	accountFormSubscription: Subscription;
	addressFormSubscription: Subscription;

	constructor(
		private route: ActivatedRoute,
		private auth: AuthenticationService,
		private stripeService: StripeService,
		private paymentsService: PaymentsService,
		private alertService: AlertService,
		private localData: LocalDataService,
		private fb: FormBuilder,
		public utils: UtilsService,
		private spinner: NgxSpinnerService,
		private fun: AngularFireFunctions,
		private modal: CustomModalService
	) {
		this.route.params.subscribe(params => {
			this.newUser = params['newuser'];
		});
	}

	ngOnInit() {
		this.kickstartCallableFunctions();

		this.spinner.show();

		if (this.auth.currentUser.displayName) {
			let fbUserName = this.auth.currentUser.displayName;
			let firstName = fbUserName.split(' ')[0];
			let lastName = fbUserName.split(' ')[1];
			this.fbUserName = {
				firstName: firstName,
				lastName: lastName
			};
		}	

		this.showPasswordField = this.auth.currentUser.providerData.some((provider) => {
			return provider.providerId === 'password';
		});
		this.userSubscription = this.auth.user.subscribe(userData => {
			if (userData) {
				console.log('User data: ', userData);
				this.userData = userData;
				if (this.userData.paymentInfo) {
					let payInfo = this.userData.paymentInfo;
					
					this.defaultPaymentMethod = {
						cardName: payInfo.name,
						expDate: 'Expires ' + payInfo.exp_month + '/' + payInfo.exp_year,
						billingAddress: this.generateBillingAddress(payInfo),
						icon: this.getCreditCardIconClass(payInfo.brand)
					};
					if (this.defaultPaymentMethod.cardName !== this.defaultPaymentNameSnapshot) {
						this.creditCardPlaceholder = { cardName: null, expDate: null, icon: null };
						this.loadingCreditCard = false;
					}
				}
				if (!this.userData.address) {
					this.userData.address = {
						streetOne: '',
						streetTwo: '',
						city: '',
						state: '',
						zip: ''
					};
				}
				
				if (this.newUser && !this.userData.displayName) {
					this.lockRoute = true;
				}

				if (!this.formsInitialized) {
					this.initializeForms();
				}
				
			}
		});

		this.accountForm = this.fb.group({
			firstName: ['', Validators.required],
			lastName: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
			primaryPhone: ['', Validators.required],
			primaryPhoneExt: [''],
			primaryPhoneType: ['', Validators.required],
			secondaryPhone: [''],
			secondaryPhoneExt: [''],
			secondaryPhoneType: ['']
		});

		this.changePasswordForm = this.fb.group({
			passwordGroup: this.fb.group(
				{
					password: ['', Validators.required],
					confirmPassword: ['', Validators.required]
				},
				{ validator: this.utils.passwordMatcher }
			)
		});

		this.addressForm = this.fb.group({
			streetOne: ['', Validators.required],
			streetTwo: '',
			city: ['', Validators.required],
			state: ['', Validators.required],
			zip: ['', Validators.required]
		});

		this.billingForm = this.fb.group({
			cardName: ['', [Validators.required]],
			billingAddressSame: ['yes'],
			addressGroup: this.fb.group({
				streetOne: ['', Validators.required],
				streetTwo: [''],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zip: ['', Validators.required]
			}, {})
		});

		this.stripeService.elements(this.elementsOptions).subscribe(elements => {
			this.elements = elements;
			if (!this.card) {
				this.card = this.elements.create('card', {
					style: cardStyle
				});
				this.card.mount('#card-info');
				this.card.on('change', (event) => {

					console.log('Card changed. Event: ', event);
					console.log('Card changed. Card: ', this.card);

					this.cardPopulated = event.empty ? false : true;
					if (event.error) {
						this.cardError = true;
						this.cardErrorMessage = event.error.message;
						this.cardValid = false;
						this.allValid = false;
					} else {
						this.cardError = false;
						this.cardErrorMessage = null;
						if (event.complete) {
							this.cardValid = true;
							if (this.billingForm.get('cardName').value.length) {
								this.allValid = true;
							}
						} else {
							this.cardValid = false;
						}
					}
				});
			}
		});

		// If there is an alert regarding either form (at the top of screen), it will go away when the user starts to type
		this.accountFormSubscription = this.accountForm.valueChanges.subscribe(data => {
			this.alertService.clear();
		});

		this.addressFormSubscription = this.addressForm.valueChanges.subscribe(data => {
			this.alertService.clear();
		});

		this.states = this.localData.getStates();
		this.cardTypes = this.localData.getCardTypes();
		this.phoneTypes = this.localData.getPhoneTypes();
		this.months = this.localData.getMonths();
		this.inputMasks = this.localData.getInputMasks();
		
		this.utils.scrollToTop();
		setTimeout(() => {
			this.spinner.hide();
		}, 500);
	}

	async kickstartCallableFunctions() {
		const res = await this.fun.httpsCallable('testCall')({ testData: 'testing this function' })
		.pipe(take(1)).toPromise();
	}

	generateBillingAddress(paymentInfo) {
		return paymentInfo.address_line1 + (paymentInfo.address_line2 ? (' ' + paymentInfo.address_line2) : '') + ', ' +
			paymentInfo.address_city + ', ' + paymentInfo.address_state + ' ' + paymentInfo.address_zip;
	}

	getCreditCardIconClass(brand) {
		switch (brand) {
			case 'American Express': 
				return 'cc amex';
			case 'MasterCard': 
				return 'cc mastercard';
			case 'Visa': 
				return 'cc visa';
			case 'Discover': 
				return 'cc discover';
			case 'Diners Club': 
				return 'cc diners club';
			case 'JCB': 
				return 'cc jcb';
			default: 
				return 'credit card';
		}
	}

	ngOnDestroy() {
		this.userSubscription.unsubscribe();
		this.accountFormSubscription.unsubscribe();
		this.addressFormSubscription.unsubscribe();
	}

	initializeForms() {
		this.accountForm.setValue({
			firstName: (this.userData.firstName ? this.userData.firstName : this.fbUserName.firstName) || '',
			lastName: (this.userData.lastName ? this.userData.lastName : this.fbUserName.lastName) || '',
			email: this.userData.email,
			primaryPhone: this.brokenPhoneNumber(this.userData.primaryPhone).base || '',
			primaryPhoneExt: this.brokenPhoneNumber(this.userData.primaryPhone).extension || '',
			primaryPhoneType: this.userData.primaryPhoneType || '',
			secondaryPhone: this.brokenPhoneNumber(this.userData.secondaryPhone).base || '',
			secondaryPhoneExt: this.brokenPhoneNumber(this.userData.secondaryPhone).extension || '',
			secondaryPhoneType: this.userData.secondaryPhoneType || ''
		});

		this.addressForm.setValue({
			streetOne: this.userData.address.streetOne || '',
			streetTwo: this.userData.address.streetTwo || '',
			city: this.userData.address.city || '',
			state: this.userData.address.state || '',
			zip: this.userData.address.zip || ''
		});

		this.populateBillingAddress(this.userData.address);

		this.formsInitialized = true;
	}

	brokenPhoneNumber(phoneNumber) {
		phoneNumber = phoneNumber || '';
		let hasExtension = phoneNumber.includes('x');
		let index = hasExtension ? (phoneNumber.indexOf('x')) : false;		
		return {
			base: hasExtension ? phoneNumber.slice(0, index) : phoneNumber,
			extension: hasExtension ? phoneNumber.slice(index, phoneNumber.length) : ''
		};
	}

	unmaskPhone(val) {
		if (!val) return false;
		let unmaskedPhone = val.replace(/\D+/g, '');
	  this.primaryPhoneError = (unmaskedPhone.length < 10) ? true : false;
	}

	async updatePaymentInfo() {
		let bForm = this.billingForm;
		let billingAddress;
		if (!this.billingForm.valid) {
			console.log('Billing form not valid');
		} else {
			const name = this.billingForm.get('cardName').value;
			this.utils.showSpinner();
			this.loadingCreditCard = true;
			this.defaultPaymentNameSnapshot = this.defaultPaymentMethod.cardName;
			if (bForm.get('billingAddressSame').value === 'yes') {		
				billingAddress = this.prepareBillingAddress(this.addressForm.value);
			} else {
				billingAddress = this.prepareBillingAddress(bForm.get('addressGroup').value);
			}
			
			if (name) {
				const data = await this.addCard(name, true);
				this.utils.hideSpinner();
				console.log('Data: ', data);
				if (data.error || data.response.address_zip_check === 'fail' || data.response.address_line1_check === 'fail') {
					let errorMessage = data.error ? data.error.message : 'The billing address or zip code associated with the card are not valid. Please correct this before proceeding.';
					return this.displayAlertModal(errorMessage);
				}
				// Successful update of card 
				this.creditCardPlaceholder = { // Temporarily display card in bottom box while FB/Stripe is loading
					cardName: data.response.name,
					expDate: 'Expires ' + data.response.exp_month + '/' + data.response.exp_year,
					icon: this.getCreditCardIconClass(data.response.brand)
				};
				this.loadingCreditCard = false;
				this.updatingCreditCard = false;
				this.cardError = false;
				this.cardErrorMessage = null;
			}
		}
	}

	async addCard(name, saveAsDefault: boolean = false) {	
		let f = this.billingForm;	
		let a = this.addressForm;
		let address = (f.get('billingAddressSame').value === 'yes') ? a.value : f.get('addressGroup').value;	
		const { token } = await this.stripeService.createToken(this.card, { name,
			address_line1: address.streetOne || '',
			address_line2: address.streetTwo || '',
			address_city: address.city || '',
			address_state: address.state || '',
			address_zip: address.zip || '',
			address_country: 'US'
		}).pipe(take(1)).toPromise();

		console.log('Token info: ', token);

		const res = await this.fun
			.httpsCallable('stripe-attachCardToUser')({ cardData: token, saveAsDefault: saveAsDefault })
			.toPromise();
		return res;
	}

	formattedCardSource(tokenInfo, billingAddress) {
		tokenInfo.card.address_line1 = billingAddress.address_line1;
		tokenInfo.card.address_line2 = billingAddress.address_line2;
		tokenInfo.card.address_city = billingAddress.address_city;
		tokenInfo.card.address_state = billingAddress.address_state;
		tokenInfo.card.address_zip = billingAddress.address_zip;
		tokenInfo.card.address_country = billingAddress.address_country;
		return tokenInfo;
	}

	prepareBillingAddress(address) {
		return {
			address_line1: address.streetOne || null,
			address_line2: address.streetTwo || null,
			address_city: address.city || null,
			address_state: address.state || null,
			address_zip: address.zip || null,
			address_country: 'US'
		};
	}

	updateDefaultCreditCard() {
		this.updatingCreditCard = true;	
		this.billingForm.reset();
		this.billingForm.patchValue({ billingAddressSame: 'yes' });
		this.populateBillingAddress(this.userData.address);
		this.card.clear();
		
		setTimeout(() => {
			this.cardNameRef.nativeElement.focus();
			this.utils.scrollToBottom();
		});
	}

	submitForm(formName: string) {
		const targetForm = this[formName];
		const updatedData: any = {};
		let fbUpdates: any = {};
		
		Object.keys(targetForm.controls).forEach(key => {
			if (key === 'primaryPhone' || key === 'secondaryPhone') {
				updatedData[key] = targetForm.controls[key].value.replace(/\D/g, '');
				if (key === 'primaryPhone') {
					updatedData[key] += ((targetForm.controls.primaryPhoneExt.value !== '') ? ('x' + targetForm.controls.primaryPhoneExt.value) : '');					
				} else {
					if (targetForm.controls[key].value !== '') {
						updatedData[key] += (targetForm.controls.secondaryPhoneExt.value !== '' ? ('x' + targetForm.controls.secondaryPhoneExt.value) : '');
					}
				}
			} else {
				if (key !== 'primaryPhoneExt' && key !== 'secondaryPhoneExt') {
					updatedData[key] = targetForm.controls[key].value;
				}
			}
		});

		this.spinner.show();
		if (formName !== 'changePasswordForm') {
			if (formName === 'addressForm') {
				fbUpdates.address = updatedData;
			} else {
				updatedData.displayName = updatedData.firstName + ' ' + updatedData.lastName;
				fbUpdates = updatedData;
			}
			this.auth.updateUser(this.userData, fbUpdates);			
			if (fbUpdates.displayName) {
				this.auth.currentUserSnap.updateProfile({
					displayName: fbUpdates.displayName,
					photoURL: this.auth.currentUserSnap.photoURL || ''
				});
			}			
			this.accountUpdateSuccess();
			this.clearAlert(2500);
		} else {
			this.passwordUpdateButtonClicked = true;
			if (targetForm.valid) {
				this.auth.updatePassword(targetForm.get('passwordGroup.password').value).then(() => {
					targetForm.reset();
					this.accountUpdateSuccess();
					this.clearAlert(2500);
				}).catch(error => {
					console.log('There was an error when updating your password. ', error);
					this.displayError(error.message, 'the account update');
				});
			} else {
				this.validateAllFormFields(targetForm);
				this.displayError('Please address the errors in red and submit again.');
			}
		}
	}

	errorLookup(key: string, field: string) {
		const errors = {
			email: 'Email address is not properly formatted',
			required: this.utils.capitalizeAndSplit(field) + ' is required'
		};
		return errors[key];
	}

	isFieldValid(field: string, formName: string) {
		const form = this[formName];		
		const formField = form.get(field);
		const fieldName = field.split('.');
		if (!formField) {
			return false;
		}
		if (!formField.valid && formField.touched) {
			const errorText = [];
			for (const err in formField.errors) {
				errorText.push(
					this.errorLookup(err, fieldName[fieldName.length - 1])
				);
			}
			return errorText;
		} else {
			return false;
		}
	}

	validateAllFormFields(formGroup?: FormGroup) {
		Object.keys(formGroup.controls).forEach(field => {
			const control = formGroup.get(field);
			if (control instanceof FormControl) {
				control.markAsTouched({ onlySelf: true });
			} else if (control instanceof FormGroup) {
				this.validateAllFormFields(control);
			}
		});
	}

	async completeRegistrationStepOne() {
		const name = this.billingForm.get('cardName').value;
		this.spinner.show();
		if (name) {
			const data = await this.addCard(name, true);
			this.utils.hideSpinner();
			console.log('Data: ', data);
			if (data.error || data.response.address_zip_check === 'fail' || data.response.address_line1_check === 'fail') {
                let errorMessage = data.error ? data.error.message : 'The billing address or zip code associated with the card are not valid. Please correct this before proceeding.';
				return this.displayAlertModal(errorMessage);
			}
			this.cardError = false;
			this.cardErrorMessage = '';
		} 
		this.completeRegistration();
	}

	async completeRegistration() {
		let forms = [
			{ name: 'accountForm', formObj: this.accountForm },
			{ name: 'addressForm', formObj: this.addressForm }
		];

		this.spinner.show();
		const data = await this.handleFormData(forms);
		
		console.log('Data after handleFormData function: ', data);
		
		if (!data.allValid) {
			this.registerSubmitClicked = true;
			this.validateAllFormFields(this.accountForm);
			this.validateAllFormFields(this.addressForm);
			this.displayError('Please address the errors in red and submit again.');
		} else {
			data.fbUpdates.optOutFromCallsAndTexts = this.models.optOutCheck;
			this.auth.updateUser(this.userData, data.fbUpdates);
			if (data.fbUpdates.displayName) { // Update the displayName on the User (Authentication, not database user)
				this.auth.currentUserSnap.updateProfile({
					displayName: data.fbUpdates.displayName,
					photoURL: this.auth.currentUserSnap.photoURL || ''
				});
			}
			this.accountUpdateSuccess(true);
			this.lockRoute = false;
			setTimeout(() => {
				this.alertService.clear();
				this.utils.navigateTo('dashboard');
			}, 2000);
		}
	}

	handleFormData(forms) {
		let fbUpdates: any = {};
		let updatedData: any;
		let allValid = true;
		forms.forEach((form) => {	
			updatedData = {};
			if (form.formObj.valid) {
				let controls = form.formObj.controls;
				Object.keys(controls).forEach(key => {					
					if (key === 'primaryPhone' || key === 'secondaryPhone') {
						updatedData[key] = controls[key].value.replace(/\D/g, '');
						if (key === 'primaryPhone') {
							updatedData[key] += ((controls.primaryPhoneExt.value !== '') ? ('x' + controls.primaryPhoneExt.value) : '');					
						} else {
							if (controls[key].value !== '') {
								updatedData[key] += (controls.secondaryPhoneExt.value !== '' ? ('x' + controls.secondaryPhoneExt.value) : '');
							}
						}
					} else {
						if (key !== 'primaryPhoneExt' && key !== 'secondaryPhoneExt') {
							updatedData[key] = controls[key].value;
						}
					}
				});
				if (form.name === 'addressForm') {
					fbUpdates.address = updatedData;
				} else {
					updatedData.displayName = updatedData.firstName + ' ' + updatedData.lastName;
					updatedData.status = 'Active';
					fbUpdates = updatedData;
				}
			} else {
				allValid = false;
			}
		});	
		return {
			allValid: allValid,
			fbUpdates: fbUpdates
		};
	}

	populateAddressForm(address, formName) {
		if (formName === 'addressForm') {
			this.addressForm.patchValue({
				streetOne: address.streetOne || null,
				city: address.city || null,
				state: address.state || null,
				zip: address.zip || null
			});
		} else {
			this.populateBillingAddress(address);
		}
	}

	populateBillingAddress(address) {
		this.billingForm.patchValue({
			addressGroup: {
				streetOne: address.streetOne || null,
				city: address.city || null,
				state: address.state || null,
				zip: address.zip || null
			}
		});
	}

	handleAddressChange(address: Address, formName) {
		this.utils.formatAddressComponents(address.address_components, (formCompatibleAddress) => {
			this.populateAddressForm(formCompatibleAddress, formName);
		});
	}

	billingAddressToggle(e) {		
		if (e === 'no') {
			this.billingForm.controls.addressGroup.reset();
		} else {
			this.populateBillingAddress(this.addressForm.value);
		}
	}

	cancelCreditCardUpdate() {
		this.updatingCreditCard = false;
		this.billingForm.reset();
		this.card.clear();
	}

	backOut() {
		if (this.lockRoute) {
			this.backToLogin();
		} else {
			this.utils.goBack();
		}
		
	}

	backToLogin() {
		this.auth.logout();
	}

	accountUpdateSuccess(registration?) {
		setTimeout(() => {
			this.spinner.hide();
			this.alertService.success(
				registration ? 'Account registration was completed successfully' : 'Account was updated successfully.'
			);
		}, 500);
	}

	displayAlertModal(message) {
		this.modal.alertModal({
			title: 'Alert',
			message: `${message}`,
			size: 'tiny'
		}, () => {
			console.log('Closed modal');
			return false;
		});
	}

	displayError(message: string, type?: string) {
		this.spinner.hide();
		this.alertService.error(
			(type ? 'There was an error during ' + type + '. ' : '') + message
		);
	}

	clearAlert(delay = 0) {
		setTimeout(() => {
			this.alertService.clear();
		}, delay);
	}
}
